import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setDefaultOptions } from "date-fns";
import { et } from "date-fns/locale";

import CameraComponent from "../../components/camera";
import QRScanner from "../../components/QRScanner";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  fetchMeterReplacementDraft,
  createMeterReplacementDraft,
  updateMeterReplacementDraft,
  deleteMeterReplacementDraft,
  fetchMetersByEstate,
  searchMeters,
  checkMeterExists,
} from "../../data/fetchData";
import { FaTimesCircle } from "react-icons/fa";
import { useNotification } from "../../context/NotificationContext";
import { isValidDecimal } from "../../utils/formatters";
import "../../styles/CustomCalendar.css";

registerLocale("et", et);
setDefaultOptions({ locale: et });

const TYPE_OPTIONS = [
  { value: "MAIN_GAUGE", label: "Põhiarvesti" },
  { value: "SUB_GAUGE", label: "Alamarvesti" },
  { value: "WATERING", label: "Kastmine" },
];

export default function Replace1() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();

  const initialDraftParam = searchParams.get("draft");
  const initialDraftId = initialDraftParam ? Number(initialDraftParam) : null;
  const [draftId, setDraftId] = useState(initialDraftId);

  useEffect(() => {
    if (initialDraftId && initialDraftId !== draftId) {
      setDraftId(initialDraftId);
    }
  }, [initialDraftId, draftId]);

  // OLD meter data (from search)
  const [oldMeterNumber, setOldMeterNumber] = useState("");
  const [oldMeterId, setOldMeterId] = useState(null);
  const [oldEstateId, setOldEstateId] = useState(null);
  const [oldClientId, setOldClientId] = useState(null);
  const [oldAddress, setOldAddress] = useState("");
  const [oldOwner, setOldOwner] = useState("");
  const [oldBrand, setOldBrand] = useState("");
  const [oldModelName, setOldModelName] = useState("");
  const [oldQn, setOldQn] = useState("");
  const [oldDiameter, setOldDiameter] = useState("");
  const [oldType, setOldType] = useState("");
  const [oldReading, setOldReading] = useState("");
  const [oldLastCertification, setOldLastCertification] = useState(new Date());
  const [meterFound, setMeterFound] = useState(false);

  // NEW meter data (complete form like Add1.js)
  const [id, setId] = useState(null);
  const [estate, setEstate] = useState(null);
  const [estateOption, setEstateOption] = useState(null);
  const [aadress, setAadress] = useState("");
  const [omanik, setOmanik] = useState(""); // Displays agreement partner name
  const [tehaseNumber, setTehaseNumber] = useState("");
  const [arvestiNait, setArvestiNait] = useState("");
  const [workDate, setWorkDate] = useState(new Date());
  const [viimaneTaatlemine, setViimaneTaatlemine] = useState(new Date());
  const [sealDate, setSealDate] = useState(new Date());
  const [sealNumber, setSealNumber] = useState("");
  const [meterIsOnConsole, setConsole] = useState("Jah");
  const [veemootjaMark, setVeemootjaMark] = useState("");
  const [qn, setQn] = useState("");
  const [diameter, setDiameter] = useState("");
  const [meterType, setMeterType] = useState("MAIN_GAUGE"); // Default to main gauge for replace
  const [notes, setNotes] = useState("");
  const [oldImages, setOldImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [meterNodeCompliant, setMeterNodeCompliant] = useState("");
  const [signature, setSignature] = useState(null);

  // Draft state management
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Lookup data
  const [brandOptions, setBrandOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diameters, setDiameters] = useState([]);

  // Load lookup data once
  useEffect(() => {
    fetchMeterBrands()
      .then(setBrandOptions)
      .catch(() => showError("Arvesti markide laadimine nurjus."));

    fetchQnoms()
      .then(setQnOptions)
      .catch(() => showError("Qn/m³ andmete laadimine nurjus."));

    fetchDiameters()
      .then(setDiameters)
      .catch(() => showError("Läbimõõtude laadimine nurjus."));
  }, [showError]);


  // Auto-fill owner/address when estate changes
  useEffect(() => {
    if (!estate) return;
    setAadress(estate.address);
    setOmanik(estate.agreement_partner_name || estate.owner_name || "");
  }, [estate]);

  // Separate function to perform meter search during draft restoration
  const performMeterSearchForDraft = useCallback(async (meterNumber) => {
    if (!meterNumber) return;

    try {
      const meterList = await searchMeters(meterNumber);


      if (!meterList.length) {

        return;
      }

      // Find exact system_number match
      let meter = meterList.find((m) => m.system_number === meterNumber);
      if (!meter) {
        meter = meterList[0];

      }

      // Replacement now supports main and sub/watering meters

      // Set the oldMeterId that was missing
      setOldMeterId(meter.id);
      // Align new meter type to old meter type by default
      if (meter.type) setMeterType(meter.type);


    } catch (error) {
      console.error("Draft meter search error:", error);
    }
  }, []);

  const hydrateFromPayload = useCallback((payload = {}, meta = null) => {
    if (!payload || typeof payload !== "object") return;

    setHasDraft(true);

    const savedAt =
      payload.draftSavedAt ||
      payload.autoSavedAt ||
      (meta?.updated_at ?? meta?.created_at ?? null);
    setDraftSavedAt(savedAt ? new Date(savedAt) : null);

    setId(payload.id ?? null);

    setOldMeterNumber(payload.oldMeterNumber || "");
    setTehaseNumber(payload.tehaseNumber || "");
    setOldMeterId(payload.oldMeterId ?? null);
    setOldEstateId(payload.oldEstateId ?? payload.estate ?? null);
    setOldClientId(payload.oldClientId ?? payload.client ?? null);
    setOldAddress(payload.oldAddress || meta?.estate_address || "");
    setOldOwner(payload.oldOwner || meta?.client_name || "");
    setOldBrand(payload.oldBrand || "");
    setOldQn(payload.oldQn || "");
    setOldDiameter(payload.oldDiameter || "");
    setOldType(payload.oldType || "");
    setOldReading(payload.oldReading || "");

    setOldLastCertification(
      payload.oldLastCertification
        ? new Date(payload.oldLastCertification)
        : new Date()
    );

    const estateObj = payload.estate
      ? {
          id: payload.estate,
          address: payload.aadress,
          owner: payload.client,
          owner_name: payload.client_name,
          agreement_partner: payload.client,
          agreement_partner_name: payload.client_name,
        }
      : null;
    setEstate(estateObj);
    setEstateOption(payload.estateOption || (estateObj
      ? {
          label: payload.aadress,
          value: estateObj.id,
          estate: estateObj,
        }
      : null));
    setAadress(payload.aadress || meta?.estate_address || "");
    setOmanik(payload.client_name || meta?.client_name || "");
    setVeemootjaMark(payload.veemootjaMark || "");
    setQn(payload.qn || "");
    setDiameter(payload.diameter || "");
    setArvestiNait(payload.arvestiNait || "");

    const parseDate = (value) =>
      value ? new Date(value) : new Date();

    setWorkDate(parseDate(payload.workDate));
    setViimaneTaatlemine(parseDate(payload.viimaneTaatlemine));
    setSealDate(parseDate(payload.sealDate));
    setSealNumber(payload.sealNumber || "");
    setConsole(payload.meterIsOnConsole || "Jah");
    setMeterType(payload.meterType || "MAIN_GAUGE");
    setNotes(payload.notes || "");
    setMeterNodeCompliant(payload.meterNodeCompliant || "");
    setOldImages(payload.oldImages || []);
    setNewImages(payload.newImages || []);
    setSignature(payload.signature || null);

    const found = payload.meterFound || Boolean(payload.oldMeterId);
    setMeterFound(found);

    if (payload.oldMeterNumber && !payload.oldMeterId) {
      performMeterSearchForDraft(payload.oldMeterNumber);
    }
  }, [performMeterSearchForDraft]);

  useEffect(() => {
    let isMounted = true;

    const stateDraft = location.state?.replacementDraft;
    if (stateDraft?.payload) {
      hydrateFromPayload(stateDraft.payload, stateDraft);
      if (!draftId || draftId !== stateDraft.id) {
        setDraftId(stateDraft.id);
        if (!searchParams.get("draft")) {
          setSearchParams({ draft: String(stateDraft.id) }, { replace: true });
        }
      }
    }

    const activeDraftId = draftId || stateDraft?.id || null;
    if (!activeDraftId) {
      setHasDraft(Boolean(stateDraft?.payload));
      if (!stateDraft?.payload) {
        setDraftSavedAt(null);
      }
      return () => {
        isMounted = false;
      };
    }

    fetchMeterReplacementDraft(activeDraftId)
      .then((draft) => {
        if (!isMounted || !draft) return;
        if (draft.payload) {
          hydrateFromPayload(draft.payload, draft);
        }
      })
      .catch((err) => {
        console.error("Failed to load replacement draft:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [draftId, location.state, hydrateFromPayload, searchParams, setSearchParams]);

  const persistDraft = useCallback(
    async ({ markSavedAt = false } = {}) => {
      const toISODate = (value) => {
        if (!value) return null;
        try {
          return new Date(value).toISOString().split("T")[0];
        } catch (error) {
          console.warn("Failed to normalise date for draft payload:", error);
          return null;
        }
      };

      const payload = {
        id,
        oldMeterNumber,
        oldMeterId,
        oldEstateId,
        oldClientId,
        oldAddress,
        oldOwner,
        oldBrand,
        oldQn,
        oldDiameter,
        oldType,
        oldReading,
        oldLastCertification: toISODate(oldLastCertification),
        aadress,
        client_name: omanik,
        client:
          estate?.agreement_partner ??
          estate?.owner ??
          oldClientId ??
          null,
        estate: estate?.id ?? oldEstateId ?? null,
        estateOption,
        tehaseNumber,
        arvestiNait,
        workDate: toISODate(workDate),
        viimaneTaatlemine: toISODate(viimaneTaatlemine),
        sealDate: toISODate(sealDate),
        sealNumber,
        meterIsOnConsole,
        veemootjaMark,
        qn,
        diameter,
        meterType,
        notes,
        meterNodeCompliant,
        oldImages,
        newImages,
        meterFound,
        workType: "Vahetus",
        signature,
      };

      if (markSavedAt) {
        payload.draftSavedAt = new Date().toISOString();
      }

      let response;
      if (draftId) {
        response = await updateMeterReplacementDraft(draftId, payload);
      } else {
        response = await createMeterReplacementDraft(payload);
        if (response?.id) {
          setDraftId(response.id);
          setSearchParams({ draft: String(response.id) }, { replace: true });
        }
      }

      if (response?.updated_at) {
        setDraftSavedAt(new Date(response.updated_at));
      } else if (markSavedAt) {
        setDraftSavedAt(new Date());
      }

      setHasDraft(true);
      return response;
    },
    [
      draftId,
      id,
      oldMeterNumber,
      oldMeterId,
      oldEstateId,
      oldClientId,
      oldAddress,
      oldOwner,
      oldBrand,
      oldQn,
      oldDiameter,
      oldType,
      oldReading,
      oldLastCertification,
      aadress,
      omanik,
      estate,
      estateOption,
      tehaseNumber,
      arvestiNait,
      workDate,
      viimaneTaatlemine,
      sealDate,
      sealNumber,
      meterIsOnConsole,
      veemootjaMark,
      qn,
      diameter,
      meterType,
      notes,
      meterNodeCompliant,
      oldImages,
      newImages,
      meterFound,
      signature,
      setDraftId,
      setSearchParams,
      setDraftSavedAt,
      setHasDraft,
    ]
  );

  // Auto-save draft periodically when form data changes
  useEffect(() => {
    if (!meterFound || !oldMeterNumber || !tehaseNumber) return;

    const autoSaveInterval = setInterval(() => {
      persistDraft().catch((err) =>
        console.debug("Replacement auto-save skipped:", err)
      );
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [meterFound, oldMeterNumber, tehaseNumber, persistDraft]);

  const handleSearch = async () => {
    if (!oldMeterNumber) {
      showError("Palun sisesta vahetamisele kuuluva arvesti number.");
      return;
    }

    try {
      const meterList = await searchMeters(oldMeterNumber);


      if (!meterList.length) {
        showError("Ei leidnud arvestit selle numbriga.");
        return;
      }

      // Find exact system_number match
      let meter = meterList.find((m) => m.system_number === oldMeterNumber);
      if (!meter) {
        meter = meterList[0];

      }


      // Replacement supports MAIN_GAUGE, SUB_GAUGE and WATERING
      // Default new meter type to the old meter's type
      if (meter.type) setMeterType(meter.type);

      // Load estate data
      let estateData = null;
      if (meter.estate && typeof meter.estate === "number") {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/estates/${meter.estate}/`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("apiToken")}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            estateData = await response.json();

          }
        } catch (error) {
          console.warn("Could not fetch estate details:", error);
        }
      } else if (meter.estate && typeof meter.estate === "object") {
        estateData = meter.estate;
      }

      // Set old meter data
      setOldMeterId(meter.id);
      setOldEstateId(meter.estate || null);

      if (estateData) {
        const clientId = estateData.agreement_partner || null;
        setOldClientId(clientId);
        setOldAddress(estateData.address_str || "");

        // Load client name and then set up new meter data
        let ownerName = "";
        if (clientId) {
          try {
            const clientResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`,
              {
                headers: {
                  Authorization: `Token ${localStorage.getItem("apiToken")}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (clientResponse.ok) {
              const clientData = await clientResponse.json();
              ownerName = clientData.full_name || "";
              setOldOwner(ownerName);
            }
          } catch (error) {
            console.warn("Could not fetch client details:", error);
            setOldOwner("");
          }
        } else {
          setOldOwner("");
        }

        // Pre-fill new meter estate data (same estate, same owner)
        const estateObj = {
          id: estateData.id,
          address: estateData.address_str,
          owner: clientId,
          owner_name: ownerName,
          agreement_partner: clientId,
          agreement_partner_name: ownerName,
        };
        setEstate(estateObj);
        setEstateOption({
          label: estateData.address_str,
          value: estateData.id,
          estate: estateObj,
        });
        
        // Set new meter address and owner fields
        setAadress(estateData.address_str || "");
        setOmanik(ownerName);
      } else {
        setOldClientId(null);
        setOldAddress("");
        setOldOwner("");
      }

      // Find brand ID from model name
      if (meter.model) {
        setOldModelName(meter.model);
        const id = findBrandIdByModel(meter.model);
        setOldBrand(id || "");
      } else {
        setOldModelName("");
        setOldBrand("");
      }

      // Find QNom ID from decimal value
      if (meter.q_nom) {
        const qnMatch = qnOptions.find(
          (q) => parseFloat(q.q_nom) === parseFloat(meter.q_nom)
        );
        setOldQn(qnMatch?.id?.toString() || "");
      } else {
        setOldQn("");
      }

      // Find Diameter ID from decimal value for old meter
      if (meter.diameter) {
        const diamMatch = diameters.find(
          (d) => parseFloat(d.diameter) === parseFloat(meter.diameter)
        );
        setOldDiameter(diamMatch?.id?.toString() || "");
      } else {
        setOldDiameter("");
      }

      // Find Diameter ID from decimal value for old meter
      if (meter.diameter) {
        const diamMatch = diameters.find(
          (d) => parseFloat(d.diameter) === parseFloat(meter.diameter)
        );
        setOldDiameter(diamMatch?.id?.toString() || "");
      } else {
        setOldDiameter("");
      }

      setOldType(meter.type || "");
      setOldReading(""); // User must enter current reading

      // Set last certification date if available
      if (meter.verification_date) {
        setOldLastCertification(new Date(meter.verification_date));
      }

      setMeterFound(true);
      showSuccess(
        "Vana arvesti andmed laetud. Palun sisestage uue arvesti andmed."
      );
    } catch (error) {
      console.error("Search error:", error);
      showError("Otsing nurjus. Palun kontrollige veemõõtja numbrit.");
    }
  };

  // Handle QR code scan for old meter number
  const handleOldMeterQRScan = async (scannedData) => {
    // Extract meter number from scanned data
    let meterNumber = scannedData.toString().trim();
    
    // If QR code contains additional data, extract meter number
    if (meterNumber.includes(':')) {
      const parts = meterNumber.split(':');
      meterNumber = parts[parts.length - 1].trim();
    }
    
    // Validate format (letters and digits)
    if (!isValidMeterNumber(meterNumber)) {
      showError('QR-koodist ei õnnestunud kehtivat arvesti numbrit lugeda');
      return;
    }
    
    // Set the old meter number and automatically trigger search
    setOldMeterNumber(meterNumber);
    
    // Automatically search for the meter data (similar to handleSearch)
    try {
      const meterList = await searchMeters(meterNumber);


      if (!meterList.length) {
        showError("QR-koodist loetud arvesti numbriga ei leidnud ühtegi arvestit.");
        return;
      }

      // Find exact system_number match
      let meter = meterList.find((m) => m.system_number === meterNumber);
      if (!meter) {
        meter = meterList[0];

      }


      // Check if this is a main gauge (only main gauges can be replaced)
      if (meter.type !== "MAIN_GAUGE") {
        showError(
          "Vahetada saab ainult peaarvesteid. See arvesti ei ole peaarvesti."
        );
        return;
      }

      // Load estate data
      let estateData = null;
      if (meter.estate && typeof meter.estate === "number") {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/estates/${meter.estate}/`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("apiToken")}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            estateData = await response.json();

          }
        } catch (error) {
          console.warn("Could not fetch estate details:", error);
        }
      } else if (meter.estate && typeof meter.estate === "object") {
        estateData = meter.estate;
      }

      // Set old meter data
      setOldMeterId(meter.id);
      setOldEstateId(meter.estate || null);

      if (estateData) {
        const clientId = estateData.agreement_partner || null;
        setOldClientId(clientId);
        setOldAddress(estateData.address_str || "");

        // Load client name and then set up new meter data
        let ownerName = "";
        if (clientId) {
          try {
            const clientResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`,
              {
                headers: {
                  Authorization: `Token ${localStorage.getItem("apiToken")}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (clientResponse.ok) {
              const clientData = await clientResponse.json();
              ownerName = clientData.full_name || "";
              setOldOwner(ownerName);
            }
          } catch (error) {
            console.warn("Could not fetch client details:", error);
            setOldOwner("");
          }
        } else {
          setOldOwner("");
        }

        // Pre-fill new meter estate data (same estate, same owner)
        const estateObj = {
          id: estateData.id,
          address: estateData.address_str,
          owner: clientId,
          owner_name: ownerName,
        };
        setEstate(estateObj);
        setEstateOption({
          label: estateData.address_str,
          value: estateData.id,
          estate: estateObj,
        });
        
        // Set new meter address and owner fields
        setAadress(estateData.address_str || "");
        setOmanik(ownerName);
      } else {
        setOldClientId(null);
        setOldAddress("");
        setOldOwner("");
      }

      // Find brand ID from model name
      if (meter.model) {
        setOldModelName(meter.model);
        const id = findBrandIdByModel(meter.model);
        setOldBrand(id || "");
      } else {
        setOldModelName("");
        setOldBrand("");
      }

      // Find QNom ID from decimal value
      if (meter.q_nom) {
        const qnMatch = qnOptions.find(
          (q) => parseFloat(q.q_nom) === parseFloat(meter.q_nom)
        );
        setOldQn(qnMatch?.id?.toString() || "");
      } else {
        setOldQn("");
      }

      setOldType(meter.type || "");
      setOldReading(""); // User must enter current reading

      // Set last certification date if available
      if (meter.verification_date) {
        setOldLastCertification(new Date(meter.verification_date));
      }

      setMeterFound(true);
      showSuccess(
        "QR-koodist loetud vana arvesti andmed laetud. Palun sisestage uue arvesti andmed."
      );
    } catch (error) {
      console.error("QR Search error:", error);
      showError("QR-koodist loetud arvesti otsing nurjus. Palun kontrollige veemõõtja numbrit.");
    }
  };

  // Handle QR code scan for new meter number
  const handleNewMeterQRScan = async (scannedData) => {
    // Extract meter number from scanned data
    let meterNumber = scannedData.toString().trim();
    
    // If QR code contains additional data, extract meter number
    if (meterNumber.includes(':')) {
      const parts = meterNumber.split(':');
      meterNumber = parts[parts.length - 1].trim();
    }
    
    // Validate format (letters and digits)
    if (isValidMeterNumber(meterNumber)) {
      setTehaseNumber(meterNumber);
      
      // Check for duplicates if estate is selected
      if (estate && estate.id) {
        try {
          const isDuplicate = await checkMeterExists(meterNumber, estate.id);
          if (isDuplicate) {
            showError(`QR-koodist loetud arvesti number ${meterNumber} on juba kinnistusel ${estate.address} kasutusel.`);
            return;
          }
        } catch (error) {
          console.error("Error checking meter duplicates:", error);
        }
      }
      
      showSuccess(`QR-koodist loetud uue arvesti number: ${meterNumber}`);
    } else {
      showError('QR-koodist ei õnnestunud kehtivat arvesti numbrit lugeda');
    }
  };

  const isValidMeterNumber = (value) =>
    typeof value === "string" && /^[A-Za-z0-9]+$/.test(value.trim());

  // Validate meter readings with up to 3 decimal places (supports comma and dot)
  const isValidReading = (v) => {
    return isValidDecimal(v, 3);
  };

  const checkMainGaugeExists = async () => {
    if (meterType !== "SUB_GAUGE" && meterType !== "WATERING") {
      return true; // Only check for sub gauges and watering meters
    }

    if (!estate || !estate.id) {
      return true; // Let the regular validation catch this
    }

    try {
      const estateMeters = await fetchMetersByEstate(estate.id);
      const hasMainGauge = estateMeters.some(
        (meter) =>
          meter.type === "MAIN_GAUGE" &&
          (meter.status === "ACTIVE" || meter.status === "CLOSED")
      );

      if (!hasMainGauge) {
        const meterTypeName =
          meterType === "SUB_GAUGE" ? "Alamarvestit" : "Kastmisarvestit";
        showError(
          `${meterTypeName} ei saa lisada kinnistule, kus pole peaarvestit. Palun lisage esmalt peaarvesti.`
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking main gauge:", error);
      showError("Peaarvesti kontrollimisel tekkis viga.");
      return false;
    }
  };

  // Quick validation for early duplicate check
  const validateForSave = async () => {
    if (!estate || !estate.id) {
      showError("Kinnistu peab olema valitud");
      return false;
    }

    // Check for duplicate meter number if provided
    if (tehaseNumber && tehaseNumber.trim() !== "") {
      if (!isValidMeterNumber(tehaseNumber)) {
        showError("Uue arvesti number võib sisaldada ainult tähti ja numbreid");
        return false;
      }

      const isDuplicate = await checkMeterExists(tehaseNumber, estate.id);
      if (isDuplicate) {
        showError(`Uue arvesti number ${tehaseNumber} on juba kinnistusel ${estate.address} kasutusel.`);
        return false;
      }
    }

    return true;
  };

  const validate = async () => {
    if (!meterFound) {
      showError("Esmalt tuleb leida vahetamisele kuuluv arvesti.");
      return false;
    }

    if (!isValidReading(oldReading)) {
      showError(
        "Vana arvesti viimane näit peab olema kehtiv number (kuni 3 kohta peale koma)."
      );
      return false;
    }

    if (!oldQn) {
      showError("Vana arvesti Qn/m³ peab olema valitud.");
      return false;
    }

    if (!isValidMeterNumber(tehaseNumber)) {
      showError("Uue arvesti number võib sisaldada ainult tähti ja numbreid.");
      return false;
    }

    if (!isValidReading(arvestiNait)) {
      showError(
        "Uue arvesti algseisu näit peab olema kehtiv number (kuni 3 kohta peale koma)."
      );
      return false;
    }

    if (!estate || !estate.id) {
      showError("Kinnistu peab olema valitud");
      return false;
    }

    if (!(estate.agreement_partner || estate.owner)) {
      showError("Kinnistul puudub lepingupartner");
      return false;
    }

    if (!veemootjaMark) {
      showError("Uue arvesti mark peab olema valitud.");
      return false;
    }

    if (!qn) {
      showError("Uue arvesti Qn/m³ peab olema valitud.");
      return false;
    }

    if (!meterType) {
      showError("Veemõõtja tüüp peab olema valitud");
      return false;
    }

    // Check main gauge exists for sub gauges
    const mainGaugeCheck = await checkMainGaugeExists();
    if (!mainGaugeCheck) {
      return false;
    }

    // Check for duplicate meter number within the estate  
    if (estate && estate.id) {
      const isDuplicate = await checkMeterExists(tehaseNumber, estate.id);
      if (isDuplicate) {
        showError(`Uue arvesti number ${tehaseNumber} on juba kinnistusel ${estate.address} kasutusel.`);
        return false;
      }
    }

    return true;
  };

  // Save draft then navigate to Replace2
  const handleNext = async () => {
    if (!(await validate())) return;
    try {
      const saved = await persistDraft({ markSavedAt: true });
      const destinationId = saved?.id || draftId;
      if (!destinationId) {
        showError("Vahetuse mustandit ei õnnestunud salvestada.");
        return;
      }
      navigate(`/Replace2?draft=${destinationId}`);
    } catch (error) {
      console.error("Failed to persist replacement draft:", error);
      showError("Vahetuse mustandi salvestamine ebaõnnestus.");
    }
  };

  // Save draft locally without creating backend record
  const saveProgress = async (e) => {
    e.preventDefault();
    if (!(await validateForSave())) return;

    try {
      const saved = await persistDraft({ markSavedAt: true });
      if (saved?.updated_at) {
        setDraftSavedAt(new Date(saved.updated_at));
      }
      showSuccess("Vahetuse mustand salvestatud edukalt!");
      navigate("/works");
    } catch (err) {
      console.error("Error saving replacement draft:", err);
      showError("Mustandi salvestamisel tekkis viga.");
    }
  };

  const handleOldCapture = (img) => setOldImages((prev) => [...prev, img]);
  const handleOldDelete = (idx) =>
    setOldImages((prev) => prev.filter((_, i) => i !== idx));

  const handleNewCapture = (img) => setNewImages((prev) => [...prev, img]);
  const handleNewDelete = (idx) =>
    setNewImages((prev) => prev.filter((_, i) => i !== idx));

  // Add function to clear draft
  const clearDraft = async () => {
    try {
      if (draftId) {
        await deleteMeterReplacementDraft(draftId);
      }
      setDraftId(null);
      setHasDraft(false);
      setDraftSavedAt(null);
      setSearchParams({}, { replace: true });
      showSuccess("Mustand kustutatud.");
      window.location.reload();
    } catch (error) {
      console.error("Failed to delete replacement draft:", error);
      showError("Mustandi kustutamine ebaõnnestus.");
    }
  };

  // Helper to map backend meter.model (free text) to a brand option ID
  const findBrandIdByModel = (modelName) => {
    if (!modelName || !(Array.isArray(brandOptions) && brandOptions.length)) return "";
    const norm = (s) =>
      String(s)
        .toLowerCase()
        .replace(/[\s_-]+/g, " ")
        .replace(/[^a-z0-9äöõü\s]/gi, "")
        .trim();
    const target = norm(modelName);
    // 1) exact (case-insensitive) name match
    let hit = brandOptions.find((b) => norm(b.name) === target);
    if (hit) return String(hit.id);
    // 2) contains/substring match either way
    hit = brandOptions.find(
      (b) => norm(b.name).includes(target) || target.includes(norm(b.name))
    );
    if (hit) return String(hit.id);
    return "";
  };

  // If options arrive later, try to resolve old brand by stored model name
  React.useEffect(() => {
    if (oldModelName && !oldBrand) {
      const id = findBrandIdByModel(oldModelName);
      if (id) setOldBrand(id);
    }
  }, [brandOptions]);

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <header className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Veemõõtja vahetus</h1>
      </header>

      {/* Draft indicator */}
      {hasDraft && draftSavedAt && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
          <div className="flex items-center justify-between">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Salvestatud mustand leitud!</strong> Viimati salvestatud: {draftSavedAt.toLocaleString('et-EE')}
                </p>
              </div>
            </div>
            <button
              onClick={clearDraft}
              className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded border border-yellow-300"
            >
              Kustuta mustand
            </button>
          </div>
        </div>
      )}

      <form onSubmit={saveProgress} className="space-y-6 mt-6">
        {/* Search for old meter */}
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h2 className="text-lg font-semibold text-red-800 mb-4">
            1. Vahetamisele kuuluv arvesti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary mb-2">
                Otsi vahetamisele kuuluvat arvesti numbrit
              </label>
              <div className="flex mb-4">
                <input
                  type="text"
                  value={oldMeterNumber}
                  onChange={(e) => setOldMeterNumber(e.target.value)}
                  placeholder="Vana arvesti number"
                  className="flex-grow p-3 border border-gray-300 rounded-l-md"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  className="px-4 py-3 border border-gray-300 rounded-r-md bg-primary text-white hover:bg-primary-hover"
                >
                  Otsi
                </button>
              </div>
              <div className="mb-4">
                <QRScanner 
                  onScan={handleOldMeterQRScan}
                  placeholder="Skaneeri vana arvesti QR-kood"
                />
              </div>
            </div>

            {meterFound && (
              <>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Kinnistu
                  </label>
                  <input
                    type="text"
                    value={oldAddress}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Lepingupartner
                  </label>
                  <input
                    type="text"
                    value={oldOwner}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Vana arvesti viimane näit
                  </label>
                  <input
                    type="text"
                    value={oldReading}
                    onChange={(e) => setOldReading(e.target.value)}
                    placeholder="Sisesta vana arvesti viimane näit"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Vana arvesti mark
                  </label>
                  <select
                    value={oldBrand}
                    onChange={(e) => setOldBrand(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  >
                    <option value="" disabled>
                      Vali mark
                    </option>
                    {brandOptions.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Vana arvesti Qn/m³
                  </label>
                  <select
                    value={oldQn}
                    onChange={(e) => setOldQn(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  >
                    <option value="" disabled>
                      Vali siit
                    </option>
                    {qnOptions.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.q_nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Vana arvesti läbimõõt
                  </label>
                  <select
                    value={oldDiameter}
                    onChange={(e) => setOldDiameter(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  >
                    <option value="" disabled>
                      Vali siit
                    </option>
                    {diameters.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.diameter}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Old meter images section */}
            {meterFound && (
              <div className="md:col-span-2 mt-4">
                <h4 className="text-md font-semibold mb-2 text-red-700">
                  Vana arvesti pildid
                </h4>
                <CameraComponent onCapture={handleOldCapture} />
                {oldImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    {oldImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={img}
                          alt={`Vana arvesti pilt ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => handleOldDelete(idx)}
                          className="absolute top-1 right-1 text-red-500 bg-white rounded-full p-1"
                        >
                          <FaTimesCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* New meter data - complete form like Add1.js */}
        {meterFound && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h2 className="text-lg font-semibold text-green-800 mb-4">
              2. Uus arvesti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div>
                <div className="mb-4">
                  <label className="block mb-2 text-secondary">Kinnistu</label>
                  <input
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
                    value={aadress}
                    readOnly
                    placeholder="Sama kinnistu, kus vana arvesti"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">Lepingupartner</label>
                  <input
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
                    value={omanik}
                    readOnly
                    placeholder="Sama omanik, kes vana arvesti"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Uue arvesti number
                  </label>
                  <input
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={tehaseNumber}
                    onChange={(e) => setTehaseNumber(e.target.value)}
                    placeholder="Sisesta uue arvesti number või skaneeri QR-kood"
                  />
                  <div className="mt-2">
                    <QRScanner 
                      onScan={handleNewMeterQRScan}
                      placeholder="Skaneeri uue arvesti QR-kood"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Uue arvesti algseisu näit
                  </label>
                  <input
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={arvestiNait}
                    onChange={(e) => setArvestiNait(e.target.value)}
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Paigaldamise kuupäev
                  </label>
                  <DatePicker
                    selected={workDate}
                    onChange={setWorkDate}
                    dateFormat="dd-MM-yyyy"
                    locale="et"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">Vali tüüp</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={meterType}
                    onChange={(e) => setMeterType(e.target.value)}
                  >
                    <option value="" disabled>
                      Vali siit
                    </option>
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Plommi number
                  </label>
                  <input
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={sealNumber}
                    onChange={(e) => setSealNumber(e.target.value)}
                  />
                </div>
              </div>

              {/* Right column */}
              <div>
                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Uue arvesti mark
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={veemootjaMark}
                    onChange={(e) => setVeemootjaMark(e.target.value)}
                  >
                    <option value="" disabled>
                      Vali mark
                    </option>
                    {brandOptions.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Vali Qn/m³
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={qn}
                    onChange={(e) => setQn(e.target.value)}
                  >
                    <option value="" disabled>
                      Vali siit
                    </option>
                    {qnOptions.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.q_nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Vali Läbimõõt
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={diameter}
                    onChange={(e) => setDiameter(e.target.value)}
                  >
                    <option value="" disabled>
                      Vali siit
                    </option>
                    {diameters.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.diameter}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Viimane taatlemine
                  </label>
                  <DatePicker
                    selected={viimaneTaatlemine}
                    onChange={setViimaneTaatlemine}
                    dateFormat="dd-MM-yyyy"
                    locale="et"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Plommimise kuupäev
                  </label>
                  <DatePicker
                    selected={sealDate}
                    onChange={setSealDate}
                    dateFormat="dd-MM-yyyy"
                    locale="et"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Arvesti konsoolil
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={meterIsOnConsole}
                    onChange={(e) => setConsole(e.target.value)}
                  >
                    <option value="Jah">Jah</option>
                    <option value="Ei">Ei</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-secondary">
                    Mõõdusõlm vastavuses
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md"
                    value={meterNodeCompliant}
                    onChange={(e) => setMeterNodeCompliant(e.target.value)}
                  >
                    <option value="">Vali siit</option>
                    <option value="Jah">Jah</option>
                    <option value="Ei">Ei</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes section */}
            <div className="grid grid-cols-1 gap-4 mt-4">
              <div>
                <label className="block mb-2 text-secondary">Märkused</label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="3"
                  placeholder="Lisainfo..."
                />
              </div>
            </div>

            {/* New meter camera section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 text-green-700">
                Uue arvesti pildid
              </h3>
              <CameraComponent onCapture={handleNewCapture} />
              {newImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {newImages.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={img}
                        alt={`Uue arvesti pilt ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => handleNewDelete(idx)}
                        className="absolute top-1 right-1 text-red-500 bg-white rounded-full p-1"
                      >
                        <FaTimesCircle size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => navigate("/works")}
            className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
          >
            Tagasi
          </button>
          <div className="flex gap-4">
            <button
              type="submit"
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Salvesta mustand
            </button>
            {meterFound && (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
              >
                Järgmine
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
